import { IIdentityData } from './IIdentity';
import { UUID } from '../../shared/UUID';
import { eWeekdays, IAdminWorkSchedule } from '../../userProfile/IUserProfile';
import {
  convertFromIPaginationSortQueryToIPaginationSortQueryRequest,
  IPaginationSortQuery,
  IPaginationSortQueryRequest,
} from '../../rest/IPaginationSortQueryRequest';

export interface IAdminUserEditRequest {
  id: UUID;
  fullName?: string;
  email?: string;
  externalEmail?: string | null;
  phoneNumber?: string | null;
  phoneExtension?: number | null;
  timeZone?: string | null;
  pronouns?: string | null;
  notes?: string | null;
  workSchedule?: Partial<Record<eWeekdays, IAdminWorkSchedule>>;
  languages?: string[];
}

// AdminUserData is the "full" version of a Pinecone user. This is like the table row in the BE.
// In Timber, this is AdminResponse
export interface IAdminUserData {
  id: UUID;
  createdAt: string;
  updatedAt: string;
  email: string;
  fullName: string;
  phoneNumber?: string;
  phoneExtension?: number;
  externalEmail?: string;
  timeZone?: string;
  pronouns?: string;
  notes?: string;
  workSchedule: Partial<Record<eWeekdays, IAdminWorkSchedule>>;
  languages?: string[];
  identity: IIdentityData;
  currentSignInAt?: string;
  signInCount: number;
  failedAttempts: number;
  unlockToken?: string;
  lockedAt?: string;
  provider?: string;
  googleId?: string;
}

// The Response is the return value of API call **for an individual admin user**. The response has the same shape as IAdminUserData
export type IAdminUserResponse = IAdminUserData;

//We can convert from IAdminUserResponse to IAdminUserData
export const convertFromIAdminUserResponseToIAdminUserData = (response: IAdminUserResponse): IAdminUserData => response;

export interface IAdminUsersQuery extends IPaginationSortQuery {
  status?: eAdminAccountStatus;
  /** fetchAll is a temporary band-aid for PP-264. It should be removed after TECH-271 properly prepares us for a type-ahead component. */
  fetchAll?: boolean;
}

export interface IGetAdminUsersParams extends IPaginationSortQueryRequest {
  status?: eAdminAccountStatus;
  includes?: eIncludesOptions[];
}

export enum eAdminAccountStatus {
  All = 'ALL',
  Active = 'ACTIVE',
  Deactivated = 'DEACTIVATED',
}

enum eIncludesOptions {
  Identity = 'IDENTITY',
  Roles = 'ROLES',
}

// fetchAll is a temporary band-aid for PP-264. It should be removed after TECH-271 properly prepares us for a type-ahead component.
// (And it's likely that this API will be replaced by Auth0 anyway)
export const convertToApiQuery = ({ status, fetchAll, ...rest }: IAdminUsersQuery): IGetAdminUsersParams => {
  if (fetchAll) {
    return convertFromIPaginationSortQueryToIPaginationSortQueryRequest({
      status,
      pageNumber: 1,
      pageSize: 1000,
      sortField: 'fullName',
      sortOrder: 'asc',
    });
  }
  return convertFromIPaginationSortQueryToIPaginationSortQueryRequest({
    status,
    includes: [eIncludesOptions.Identity, eIncludesOptions.Roles],
    ...rest,
  });
};
